import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, BellRing, CheckCheck, Smartphone } from 'lucide-react';
import { API_BASE } from '../config/api';
import './VistaAdminLayout.css';

const normalizarNotificaciones = (items) => (Array.isArray(items) ? items : []);

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  try {
    return new Date(fecha).toLocaleString('es-PY');
  } catch {
    return '';
  }
};

const urlBase64AUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export default function CentroNotificaciones({ modo, rol: rolProp, usuarioId: usuarioIdProp }) {
  const [abierto, setAbierto] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [pushEstado, setPushEstado] = useState('idle');
  const contenedorRef = useRef(null);

  const rol = useMemo(() => {
    if (rolProp) return rolProp;
    const rolLocal = (localStorage.getItem('rolAdmin') || '').toLowerCase();
    if (modo === 'tecnico' || rolLocal === 'tecnico') return 'tecnico';
    return 'admin';
  }, [modo, rolProp]);

  const usuarioId = useMemo(
    () => Number(usuarioIdProp || localStorage.getItem('adminId') || 0),
    [usuarioIdProp]
  );

  const noLeidas = useMemo(
    () => notificaciones.filter((item) => Number(item.leida) === 0).length,
    [notificaciones]
  );

  const cargarNotificaciones = async () => {
    if (!usuarioId) return;
    try {
      const params = new URLSearchParams({ rol, usuario_id: String(usuarioId) });
      const res = await fetch(`${API_BASE}/api/notificaciones?${params.toString()}`);
      if (!res.ok) throw new Error('No se pudieron cargar notificaciones');
      const data = await res.json();
      setNotificaciones(normalizarNotificaciones(data));
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    }
  };

  useEffect(() => {
    cargarNotificaciones();
  }, [rol, usuarioId]);

  useEffect(() => {
    if (!usuarioId) return undefined;

    const params = new URLSearchParams({ rol, usuario_id: String(usuarioId) });
    const events = new EventSource(`${API_BASE}/api/notificaciones/stream?${params.toString()}`);

    events.addEventListener('notificacion', (event) => {
      try {
        const item = JSON.parse(event.data);
        setNotificaciones((prev) => [item, ...prev].slice(0, 60));
      } catch (error) {
        console.error('Error procesando notificación:', error);
      }
    });

    return () => events.close();
  }, [rol, usuarioId]);

  useEffect(() => {
    const onClick = (event) => {
      if (contenedorRef.current && !contenedorRef.current.contains(event.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const marcarLeida = async (id) => {
    setNotificaciones((prev) =>
      prev.map((item) => (Number(item.id) === Number(id) ? { ...item, leida: 1 } : item))
    );

    try {
      await fetch(`${API_BASE}/api/notificaciones/${id}/leida`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rol, usuario_id: usuarioId }),
      });
    } catch (error) {
      console.error('Error marcando notificación:', error);
    }
  };

  const marcarTodas = async () => {
    setNotificaciones((prev) => prev.map((item) => ({ ...item, leida: 1 })));
    try {
      await fetch(`${API_BASE}/api/notificaciones/marcar-todas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rol, usuario_id: usuarioId }),
      });
    } catch (error) {
      console.error('Error marcando notificaciones:', error);
    }
  };

  const guardarSuscripcionPush = async (subscription) => {
    const res = await fetch(`${API_BASE}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rol, usuario_id: usuarioId, subscription }),
    });

    if (!res.ok) throw new Error('No se pudo guardar la suscripción Push');
  };

  useEffect(() => {
    let cancelado = false;

    const detectarPushActivo = async () => {
      if (!usuarioId) return;
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        if (!cancelado) setPushEstado('unsupported');
        return;
      }

      if (Notification.permission === 'denied') {
        if (!cancelado) setPushEstado('denied');
        return;
      }

      if (Notification.permission !== 'granted') return;

      try {
        const registration = await navigator.serviceWorker.register('/fbdesk-sw.js');
        const subscription = await registration.pushManager.getSubscription();

        if (!cancelado && subscription) {
          setPushEstado('enabled');
          guardarSuscripcionPush(subscription).catch((error) =>
            console.error('Error sincronizando Push existente:', error)
          );
        }
      } catch (error) {
        console.error('Error detectando Push existente:', error);
      }
    };

    detectarPushActivo();
    return () => {
      cancelado = true;
    };
  }, [rol, usuarioId]);

  const activarPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPushEstado('unsupported');
      return;
    }

    try {
      setPushEstado('loading');
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') {
        setPushEstado('denied');
        return;
      }

      const keyRes = await fetch(`${API_BASE}/api/push/vapid-public-key`);
      const keyData = await keyRes.json();
      if (!keyData?.habilitado || !keyData?.publicKey) {
        setPushEstado('unavailable');
        return;
      }

      const registration = await navigator.serviceWorker.register('/fbdesk-sw.js');
      const existente = await registration.pushManager.getSubscription();
      const subscription =
        existente ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64AUint8Array(keyData.publicKey),
        }));

      await guardarSuscripcionPush(subscription);
      setPushEstado('enabled');
    } catch (error) {
      console.error('Error activando Push:', error);
      setPushEstado('error');
    }
  };

  const textoPush = {
    idle: 'Activar Push',
    loading: 'Activando...',
    enabled: 'Push activo',
    denied: 'Permiso bloqueado',
    unsupported: 'No soportado',
    unavailable: 'No disponible',
    error: 'Reintentar Push',
  }[pushEstado];

  return (
    <div className="notificaciones" ref={contenedorRef}>
      <button
        type="button"
        className={`notificaciones-btn ${noLeidas > 0 ? 'tiene-alertas' : ''}`}
        onClick={() => setAbierto((actual) => !actual)}
        title="Notificaciones"
      >
        {noLeidas > 0 ? <BellRing size={18} /> : <Bell size={18} />}
        {noLeidas > 0 && <span className="notificaciones-contador">{noLeidas}</span>}
      </button>

      {abierto && (
        <div className="notificaciones-panel">
          <div className="notificaciones-top">
            <div>
              <h3>Notificaciones</h3>
              <p>{noLeidas} sin leer</p>
            </div>
            <button type="button" onClick={marcarTodas} title="Marcar todas como leídas">
              <CheckCheck size={17} />
            </button>
          </div>

          <button
            type="button"
            className={`notificaciones-push ${pushEstado === 'enabled' ? 'activo' : ''}`}
            onClick={activarPush}
            disabled={pushEstado === 'loading' || pushEstado === 'enabled'}
          >
            <Smartphone size={16} />
            {textoPush}
          </button>

          <div className="notificaciones-lista">
            {notificaciones.length === 0 ? (
              <div className="notificaciones-vacio">Sin notificaciones recientes.</div>
            ) : (
              notificaciones.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`notificaciones-item ${Number(item.leida) === 0 ? 'no-leida' : ''}`}
                  onClick={() => marcarLeida(item.id)}
                >
                  <span>{item.titulo}</span>
                  <p>{item.mensaje}</p>
                  <small>{formatearFecha(item.fecha)}</small>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
